import axios from "axios";
/**
 * Stable auth-svc endpoints (CO 048 freezes these — do not change shapes).
 * One source of truth for the four React apps that previously each carried a
 * copy of these calls.
 */
const ENDPOINTS = {
    register: "/managers/register",
    login: "/managers/login",
    google: "/managers/google",
    googleVerify: "/managers/google/verify",
    refresh: "/managers/refresh",
    me: "/managers/me",
    emailVerify: "/managers/email/verify",
    passwordResetRequest: "/managers/password/reset-request",
    passwordReset: "/managers/password/reset",
};
const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const DEFAULT_TIMEOUT_MS = 30_000;
/**
 * Build an auth client bound to a backend `baseURL`. Carries the same
 * bearer-token request interceptor and 401 → refresh response interceptor the
 * per-app clients had, parameterized so the package never reads env directly.
 */
export function createAuthClient(config) {
    const http = axios.create({
        baseURL: config.baseURL,
        timeout: config.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
    });
    http.interceptors.request.use((request) => {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token) {
            request.headers.Authorization = `Bearer ${token}`;
        }
        return request;
    }, (error) => Promise.reject(error));
    http.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 &&
            originalRequest &&
            !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
                if (refreshToken) {
                    const response = await axios.post(`${config.baseURL}${ENDPOINTS.refresh}`, { refreshToken });
                    const { accessToken, refreshToken: newRefreshToken } = response.data.data;
                    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
                    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
                    if (!originalRequest.headers) {
                        originalRequest.headers = {};
                    }
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return http(originalRequest);
                }
            }
            catch (refreshError) {
                localStorage.removeItem(ACCESS_TOKEN_KEY);
                localStorage.removeItem(REFRESH_TOKEN_KEY);
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error);
    });
    return {
        http,
        registerManager: async (data) => {
            const response = await http.post(ENDPOINTS.register, data);
            return response.data.data;
        },
        loginManager: async (data) => {
            const response = await http.post(ENDPOINTS.login, data);
            return response.data.data;
        },
        loginWithGoogle: async (credential) => {
            const response = await http.post(ENDPOINTS.google, { credential });
            return response.data.data;
        },
        registerWithGoogle: async (credential) => {
            // Same endpoint as login; loginOnly:false allows account creation.
            const response = await http.post(ENDPOINTS.google, {
                credential,
                loginOnly: false,
            });
            return response.data.data;
        },
        verifyGoogleToken: async (credential) => {
            const response = await http.post(ENDPOINTS.googleVerify, { credential });
            return response.data.data;
        },
        refreshToken: async (refreshToken) => {
            const response = await http.post(ENDPOINTS.refresh, { refreshToken });
            return response.data.data;
        },
        getCurrentManager: async () => {
            const response = await http.get(ENDPOINTS.me);
            return response.data.data;
        },
        verifyEmail: async (token) => {
            const response = await http.post(ENDPOINTS.emailVerify, { token });
            return response.data.data;
        },
        requestPasswordReset: async (email) => {
            await http.post(ENDPOINTS.passwordResetRequest, { email });
        },
        resetPassword: async (token, newPassword) => {
            await http.post(ENDPOINTS.passwordReset, { token, newPassword });
        },
    };
}
export { ENDPOINTS, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY };
